import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "./theme";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ErrorBoundary from "./components/ErrorBoundary";

// scripts/generate-static-seo-pages.js bakes a title, meta tags and a text
// summary into each page for crawlers that don't run JS. The app renders its
// own head tags (SEOHead) and visible H1, so drop the static copies here --
// otherwise the rendered page carries duplicate canonicals/titles and a second,
// hidden H1. JSON-LD is not marked and stays: the app doesn't emit it to <head>.
document.querySelectorAll("[data-static-seo]").forEach((el) => el.remove());

// Static JSON-LD is kept (some pages' components render no schema), but once
// the app renders its own block of the same @type, the static copy goes --
// otherwise the rendered page carries two FAQPage/Organization/... blocks.
const ldType = (el) => {
  try {
    return JSON.stringify(JSON.parse(el.textContent)["@type"]);
  } catch {
    return null;
  }
};
const staticLdObserver = new MutationObserver(() => {
  const staticLd = document.querySelectorAll('script[type="application/ld+json"][data-static-ld]');
  if (!staticLd.length) {
    staticLdObserver.disconnect();
    return;
  }
  const appTypes = new Set(
    [...document.querySelectorAll('script[type="application/ld+json"]:not([data-static-ld])')].map(ldType)
  );
  staticLd.forEach((el) => {
    if (appTypes.has(ldType(el))) el.remove();
  });
});
staticLdObserver.observe(document.body, { childList: true, subtree: true });
// Pages whose app never renders a given @type keep that static block; stop
// watching once the page (including slow API-driven content) has settled.
setTimeout(() => staticLdObserver.disconnect(), 30000);

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ErrorBoundary>
      </ThemeProvider>
    </HelmetProvider>
  </React.StrictMode>
);
